import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Repeat, Plus, Loader2, Search, Pencil, Trash2, CalendarClock, Coins,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../ui/sheet";
import { toast } from "sonner";
import { formatCameraAssegnata } from "../../utils/formatCamera";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENZE = [
  { value: "mensile", label: "Mensile" },
  { value: "bimestrale", label: "Bimestrale" },
  { value: "trimestrale", label: "Trimestrale" },
  { value: "semestrale", label: "Semestrale" },
  { value: "annuale", label: "Annuale" },
];

const STATO_BADGE = {
  attiva: "bg-emerald-100 text-emerald-800",
  sospesa: "bg-slate-100 text-slate-600",
  terminata: "bg-red-100 text-red-700",
};

const FREQ_BADGE = {
  mensile: "bg-emerald-50 text-emerald-700 border-emerald-200",
  bimestrale: "bg-violet-50 text-violet-700 border-violet-200",
  trimestrale: "bg-blue-50 text-blue-700 border-blue-200",
  semestrale: "bg-amber-50 text-amber-700 border-amber-200",
  annuale: "bg-slate-50 text-slate-700 border-slate-200",
};

const RICORRENZE_KPI_CONFIG = [
  {
    key: "ricorrenzeAttive",
    icon: Repeat,
    iconBg: "bg-emerald-500",
    valueColor: "text-emerald-600",
    label: "Ricorrenze attive",
    sub: "Voci attive",
    format: "number",
  },
  {
    key: "inScadenza",
    icon: CalendarClock,
    iconBg: "bg-amber-500",
    valueColor: "text-amber-600",
    label: "In scadenza",
    sub: "Nei prossimi 30 giorni",
    format: "number",
  },
  {
    key: "importoPrevistoMese",
    icon: Coins,
    iconBg: "bg-blue-500",
    valueColor: "text-blue-600",
    label: "Importo previsto mese",
    sub: "Totale ricorrenze da fatturare",
    format: "euro",
  },
];

function RicorrenzeKpiCard({ cfg, kpi }) {
  const Icon = cfg.icon;
  const raw = kpi[cfg.key] ?? 0;
  const display = cfg.format === "euro"
    ? `€ ${Number(raw).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : String(raw);

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col min-h-[108px]">
      <div className="p-3.5 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div
            className={`w-10 h-10 rounded-lg ${cfg.iconBg} flex items-center justify-center shrink-0 shadow-sm`}
          >
            <Icon className="w-[18px] h-[18px] text-white" strokeWidth={2} />
          </div>
          <span className={`text-2xl font-bold leading-none tabular-nums ${cfg.valueColor}`}>
            {display}
          </span>
        </div>
        <h3 className="mt-2.5 text-sm font-bold text-slate-900 leading-snug">{cfg.label}</h3>
        <p className="text-xs text-slate-500 mt-0.5">{cfg.sub}</p>
      </div>
    </div>
  );
}

function formatData(d) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

const EMPTY_FORM = {
  ospiteId: "",
  listinoVoceId: "",
  descrizione: "",
  importo: "",
  frequenza: "mensile",
  dataPrimaApplicazione: new Date().toISOString().split("T")[0],
  dataFine: "",
  stato: "attiva",
};

export default function PrimaNotaRicorrenzeTab({
  token,
  meseCorrente,
  ospitiAttivi,
  listinoVoci,
  filtroOspite,
}) {
  const [ricorrenze, setRicorrenze] = useState([]);
  const [kpi, setKpi] = useState({ ricorrenzeAttive: 0, inScadenza: 0, importoPrevistoMese: 0 });
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [filtroTipo, setFiltroTipo] = useState("tutte");
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: search || undefined, stato: filtroStato !== "tutti" ? filtroStato : undefined };
      if (filtroOspite && filtroOspite !== "tutti") params.ospiteId = filtroOspite;
      const [rRes, kRes] = await Promise.all([
        axios.get(`${API_URL}/api/prima-nota/ricorrenze`, {
          headers: { Authorization: `Bearer ${token}` }, params,
        }),
        axios.get(`${API_URL}/api/prima-nota/ricorrenze/kpi`, {
          headers: { Authorization: `Bearer ${token}` }, params: { mese: meseCorrente },
        }),
      ]);
      setRicorrenze(rRes.data.ricorrenze || []);
      setKpi(kRes.data);
    } catch {
      toast.error("Errore caricamento ricorrenze");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token, meseCorrente, filtroOspite, filtroStato, search]); // eslint-disable-line

  const ospiteMap = useMemo(() => {
    const m = {};
    ospitiAttivi.forEach((o) => { m[o.id] = o; });
    return m;
  }, [ospitiAttivi]);

  const vociServizi = (listinoVoci || []).filter((v) => v.attivo !== false);

  const openNew = () => {
    setEditId(null);
    setForm({
      ...EMPTY_FORM,
      ospiteId: filtroOspite !== "tutti" ? filtroOspite : "",
      dataPrimaApplicazione: new Date().toISOString().split("T")[0],
    });
    setSheetOpen(true);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      ospiteId: r.ospiteId,
      listinoVoceId: r.listinoVoceId || "",
      descrizione: r.descrizione || "",
      importo: String(r.importo ?? ""),
      frequenza: r.frequenza || "mensile",
      dataPrimaApplicazione: r.dataPrimaApplicazione || "",
      dataFine: r.dataFine || "",
      stato: r.stato || "attiva",
    });
    setSheetOpen(true);
  };

  const onSelectVoce = (voceId) => {
    const v = vociServizi.find((x) => x.id === voceId);
    setForm((f) => ({
      ...f,
      listinoVoceId: voceId,
      descrizione: v ? v.nome : f.descrizione,
      importo: v ? String(v.prezzoUnitario) : f.importo,
    }));
  };

  const handleSave = async () => {
    if (!form.ospiteId) return toast.error("Seleziona ospite");
    if (!form.descrizione.trim()) return toast.error("Inserisci la voce");
    if (!form.importo || parseFloat(form.importo) < 0) return toast.error("Importo non valido");
    setSaving(true);
    try {
      const payload = {
        ospiteId: form.ospiteId,
        listinoVoceId: form.listinoVoceId || null,
        descrizione: form.descrizione.trim(),
        importo: parseFloat(form.importo),
        frequenza: form.frequenza,
        dataPrimaApplicazione: form.dataPrimaApplicazione,
        dataFine: form.dataFine || null,
        stato: form.stato,
      };
      if (editId) {
        await axios.put(`${API_URL}/api/prima-nota/ricorrenze/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Ricorrenza aggiornata");
      } else {
        await axios.post(`${API_URL}/api/prima-nota/ricorrenze`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Ricorrenza salvata");
      }
      setSheetOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa ricorrenza?")) return;
    try {
      await axios.delete(`${API_URL}/api/prima-nota/ricorrenze/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Eliminata");
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <>
      <Card className="border border-slate-200/80 shadow-lg rounded-2xl bg-white">
        <CardHeader className="border-b border-slate-200 bg-white rounded-t-2xl flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-4">
          <div>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <Repeat className="w-5 h-5 text-emerald-600" />
              Ricorrenze di Fatturazione
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              Configura una sola volta i costi periodici da inserire automaticamente nelle fatture degli ospiti.
            </p>
          </div>
          <Button className="bg-emerald-600 hover:bg-emerald-700 shrink-0" onClick={openNew}>
            <Plus className="w-4 h-4 mr-1" /> Nuova ricorrenza
          </Button>
        </CardHeader>
        <CardContent className="p-4 md:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5 pb-5 border-b border-slate-200">
            {RICORRENZE_KPI_CONFIG.map((cfg) => (
              <RicorrenzeKpiCard key={cfg.key} cfg={cfg} kpi={kpi} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-[140px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti gli stati</SelectItem>
                <SelectItem value="attiva">Attiva</SelectItem>
                <SelectItem value="sospesa">Sospesa</SelectItem>
                <SelectItem value="terminata">Terminata</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Cerca voce, ospite..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-xl">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Ospite</TableHead>
                    <TableHead>Voce</TableHead>
                    <TableHead>Frequenza</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Prossima generazione</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ricorrenze.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        Nessuna ricorrenza
                      </TableCell>
                    </TableRow>
                  ) : (
                    ricorrenze.map((r) => {
                      const o = ospiteMap[r.ospiteId];
                      return (
                        <TableRow key={r.id}>
                          <TableCell>
                            <span className="font-medium">{o?.nome_completo || "—"}</span>
                            {o?.camera && (
                              <span className="block text-xs text-slate-400">
                                {formatCameraAssegnata(o.camera) || o.camera}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{r.descrizione}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={FREQ_BADGE[r.frequenza] || ""}>
                              {FREQUENZE.find((f) => f.value === r.frequenza)?.label || r.frequenza}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            € {Number(r.importo || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{formatData(r.prossimaGenerazione)}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${STATO_BADGE[r.stato] || STATO_BADGE.attiva}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${r.stato === "attiva" ? "bg-emerald-500" : r.stato === "terminata" ? "bg-red-500" : "bg-slate-400"}`} />
                              {(r.stato || "attiva").charAt(0).toUpperCase() + (r.stato || "attiva").slice(1)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(r.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editId ? "Modifica ricorrenza" : "Nuova ricorrenza"}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>Ospite *</Label>
              <Select value={form.ospiteId} onValueChange={(v) => setForm((f) => ({ ...f, ospiteId: v }))} disabled={!!editId}>
                <SelectTrigger><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                <SelectContent>
                  {ospitiAttivi.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.nome_completo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Voce *</Label>
              <Select value={form.listinoVoceId} onValueChange={onSelectVoce}>
                <SelectTrigger><SelectValue placeholder="Seleziona dal listino" /></SelectTrigger>
                <SelectContent>
                  {vociServizi.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nome} — € {v.prezzoUnitario}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Descrizione voce"
                value={form.descrizione}
                onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Importo (€) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
                <Input className="pl-8" type="number" step="0.01" value={form.importo}
                  onChange={(e) => setForm((f) => ({ ...f, importo: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Frequenza *</Label>
              <Select value={form.frequenza} onValueChange={(v) => setForm((f) => ({ ...f, frequenza: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENZE.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data prima applicazione *</Label>
              <Input type="date" value={form.dataPrimaApplicazione}
                onChange={(e) => setForm((f) => ({ ...f, dataPrimaApplicazione: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Fine ricorrenza (opzionale)</Label>
              <Input type="date" value={form.dataFine}
                onChange={(e) => setForm((f) => ({ ...f, dataFine: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Attiva</Label>
              <Switch
                checked={form.stato === "attiva"}
                onCheckedChange={(c) => setForm((f) => ({ ...f, stato: c ? "attiva" : "sospesa" }))}
              />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Salva ricorrenza
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
